import { isoDateTime, z } from "../zod.js";
import { Locale, localeKeys } from "../locales.js";
import { Currency, Uuid } from "../identity/admin-schemas.js";
import { TERMINOLOGY_KEYS } from "../terminology.js";

// Module 02 — Onboarding: the P1 contract DTOs (spec 02 §5 request/response
// bodies) + the RFC 9457 problem-code catalog (spec 02 §5/§9). PHASE 1 =
// schemas only; the OpenAPI PATH registration (onboarding/register.ts) and the
// generated api-client routes land in Phase 2 (deferred so no controller-less
// route breaks the contract/coverage gates). These schemas are therefore NOT
// yet referenced by any registered path — they compile + export as the typed
// contract the services + controllers will bind to.
//
// Conventions carried from catalogue/schemas.ts: uuid ids via `Uuid`; money as
// integer minor units (`z.number().int()`, never a float); business
// dates as `format: date` strings; cost/PII fields the server redacts are
// `.optional()` (omitted, never nulled — the response envelope lists
// `redacted_fields`, spec 02 §7).

// --- shared scalars ---

/** ISO-8601 business date (facility timezone), e.g. target_go_live_date. */
const BusinessDate = z.string().openapi({ format: "date", example: "2026-08-15" });
/** Integer minor units (money) — never a float (the money convention). */
const MinorMoney = z.number().int();

// --- session + steps (spec 02 §2.1, §2.2, §5) ---

export const OnboardingSessionStatus = z.enum(["draft", "in_progress", "ready_for_review", "live"]);
export const OnboardingStepStatus = z.enum(["not_started", "in_progress", "completed"]);

export const OnboardingSession = z
  .object({
    id: Uuid,
    status: OnboardingSessionStatus,
    current_step: z.number().int().min(1).max(5),
    vertical_key: z.string().nullable(),
    pack_version: z.string().nullable(),
    target_go_live_date: BusinessDate.nullable(),
    completeness_score: z.number().int().min(0).max(100),
    started_by_user_id: Uuid,
    started_at: isoDateTime(),
    updated_at: isoDateTime(),
    went_live_at: isoDateTime().nullable(),
    went_live_by_user_id: Uuid.nullable(),
  })
  .openapi("OnboardingSession");

export const OnboardingStepState = z
  .object({
    id: Uuid,
    step_no: z.number().int().min(1).max(5),
    status: OnboardingStepStatus,
    payload: z.record(z.string(), z.unknown()),
    completed_by_user_id: Uuid.nullable(),
    completed_at: isoDateTime().nullable(),
    updated_at: isoDateTime(),
  })
  .openapi("OnboardingStepState");

/** The compact checklist summary embedded in the session response (§5). */
export const OnboardingChecklistSummary = z
  .object({
    score: z.number().int().min(0).max(100),
    blocking_unmet: z.array(z.string()),
  })
  .openapi("OnboardingChecklistSummary");

export const OnboardingSessionResponse = z
  .object({
    session: OnboardingSession,
    steps: z.array(OnboardingStepState),
    checklist_summary: OnboardingChecklistSummary,
  })
  .openapi("OnboardingSessionResponse");

export const OnboardingSessionPatchRequest = z
  .object({ target_go_live_date: BusinessDate.nullable() })
  .openapi("OnboardingSessionPatchRequest");

/**
 * Save a step's scratch payload (§5 PUT /steps/:step_no/payload). `updated_at`
 * is the optimistic-concurrency token (spec 02 §9.1): a stale value → 409
 * `STALE_PAYLOAD` with the server copy. Optional so the first save (no prior
 * state) can omit it.
 */
export const OnboardingStepPayloadPutRequest = z
  .object({
    payload: z.record(z.string(), z.unknown()),
    updated_at: isoDateTime().optional(),
  })
  .openapi("OnboardingStepPayloadPutRequest");

// --- vertical packs (spec 02 §2.11, §5) ---

export const VerticalPackSummary = z
  .object({
    key: z.string(),
    version: z.string(),
    name: z.record(z.string(), z.string()),
    description: z.record(z.string(), z.string()),
    counts_by_kind: z.record(z.string(), z.number().int()),
  })
  .openapi("VerticalPackSummary");

export const VerticalPackListResponse = z
  .object({ items: z.array(VerticalPackSummary) })
  .openapi("VerticalPackListResponse");

/**
 * The full pack content for preview (§5 GET /verticals/:key → "the VerticalPack
 * shape"). The authoritative zod contract for a pack lives in
 * packages/verticals (spec 02 §2.11); the API serves it read-only from the
 * bundle. Modelled here as a passthrough object so the contract does not
 * duplicate (and drift from) the verticals schema — Phase 2 wires the exact
 * shape through the api-client from the verticals package.
 */
export const VerticalPackDetail = z.record(z.string(), z.unknown()).openapi("VerticalPackDetail");

export const PackInstallStatus = z.enum(["installing", "installed", "failed", "superseded"]);

export const VerticalPackInstall = z
  .object({
    id: Uuid,
    pack_key: z.string(),
    pack_version: z.string(),
    status: PackInstallStatus,
    seeded_counts: z.record(z.string(), z.number().int()),
    error: z.record(z.string(), z.unknown()).nullable(),
    installed_by_user_id: Uuid,
    created_at: isoDateTime(),
    finished_at: isoDateTime().nullable(),
    /** sha256 (hex) of the pack this install is based on (D-343). */
    pack_digest: z.string().regex(/^[0-9a-f]{64}$/u),
    /** `install` (fresh or forced) or `update` (a previewed update, D-343). */
    mode: z.enum(["install", "update"]),
  })
  .openapi("VerticalPackInstall");

// --- the previewed pack update (spec 02 §3.2a, D-343) ---

/** One pack object named by its kind and pack ref. */
export const PackObjectRef = z
  .object({
    kind: z.string().min(1).max(64),
    key: z.string().min(1).max(200),
  })
  .openapi("PackObjectRef");

/** One object in the update preview: what the bundle does to it against the
 * tenant's snapshot, and what an apply would do. `applies` objects are taken
 * by every apply; an `acceptable` change only when ticked; `customized`,
 * `adopt_only` and `kept` are listed and never touched; `pending_module` waits
 * on an owning module this deployment does not run. */
export const PackUpdatePreviewEntry = z
  .object({
    object_kind: z.string(),
    key: z.string(),
    change: z.enum(["added", "changed", "removed", "missing"]),
    fields: z.array(z.string()),
    disposition: z.enum([
      "applies",
      "acceptable",
      "customized",
      "adopt_only",
      "kept",
      "pending_module",
    ]),
    owner_module: z.string(),
  })
  .openapi("PackUpdatePreviewEntry");

export const PackUpdatePreview = z
  .object({
    install_id: Uuid,
    pack_key: z.string(),
    installed_version: z.string(),
    installed_digest: z.string(),
    available_version: z.string(),
    available_digest: z.string(),
    update_available: z.boolean(),
    live: z.boolean(),
    can_apply: z.boolean(),
    counts: z.object({
      added: z.number().int().min(0),
      changed: z.number().int().min(0),
      removed: z.number().int().min(0),
      missing: z.number().int().min(0),
      acceptable: z.number().int().min(0),
    }),
    changes: z.array(PackUpdatePreviewEntry),
  })
  .openapi("PackUpdatePreview");

/** POST body: the digest the admin previewed (refused 409 when the deployed
 * pack moved since) and the changes to take. */
export const PackUpdateRequest = z
  .object({
    available_digest: z.string().regex(/^[0-9a-f]{64}$/u),
    accept: z.array(PackObjectRef).max(500),
  })
  .openapi("PackUpdateRequest");

export const PackInstallRequest = z
  .object({ force: z.boolean().optional() })
  .openapi("PackInstallRequest");

export const VerticalPackListWrapper = z
  .object({ items: z.array(VerticalPackSummary) })
  .openapi("VerticalPackListWrapper");

export const SeedDisposition = z.enum(["seeded", "snapshot", "skipped"]);

/** One pack object_kind's disposition in the current install (spec 02 §3.2):
 * seeded (materialized via an owning-module service + a pack_seed_links row),
 * snapshot (served from pack_snapshot, §2.4), or skipped (staged — its owning
 * module is not wired yet; `owner_module` names it, never a fake seed).
 *
 * A `skipped` row carried an English `reason` SENTENCE until D-225 ("The
 * 'waste_category' seed kind is owned by waste & recovery (Module 05), which
 * is not wired into this deployment yet…"). It is removed rather than
 * translated: it restated `disposition` + `owner_module`, "it seeds when that
 * module ships" is the DEFINITION of `skipped` and not a per-row fact, and the
 * only client mapped it into a field it never rendered — the breakdown already
 * composes its localized "pending <module>" line from `owner_module`. */
export const VerticalPackInstallKind = z
  .object({
    object_kind: z.string(),
    disposition: SeedDisposition,
    count: z.number().int().min(0),
    owner_module: z.string(),
  })
  .openapi("VerticalPackInstallKind");

export const VerticalPackSeedLinkSummary = z
  .object({
    object_kind: z.string(),
    owner_module: z.string(),
    count: z.number().int().min(0),
  })
  .openapi("VerticalPackSeedLinkSummary");

/** GET /v1/verticals/installs/current (spec 02 §5): the install row + the live
 * per-kind disposition breakdown + the materialized pack_seed_links summary. */
export const VerticalPackInstallCurrentResponse = z
  .object({
    install: VerticalPackInstall,
    kinds: z.array(VerticalPackInstallKind),
    seed_links: z.array(VerticalPackSeedLinkSummary),
  })
  .openapi("VerticalPackInstallCurrentResponse");

// --- terminology (spec 02 §2.5, §5) ---

export const TerminologyLocale = Locale;
/**
 * Resolution origin of an EFFECTIVE terminology label (spec 02 §2.5 order:
 * tenant override → pack override → base string). `base` was added in Phase 2a:
 * GET /v1/terminology "resolve[s] every term_key" (spec 02 §5), so a key with no
 * tenant/pack row resolves to the base label and must report its true origin —
 * mislabelling a base string as `pack` would lie to the S9 settings table.
 * Pack-install (a later slice) writes `pack` rows; tenant edits write `tenant`.
 */
export const TerminologySource = z.enum(["base", "pack", "tenant"]);

/** The frozen closed catalog (terminology.ts, spec 02 §2.5) — the DTO's
 * `term_key` is the exact 61-key union, not a loose string, so the contract is
 * as strong as the DB `terminology_overrides.term_key` service check + the
 * verticals lint. */
export const TerminologyKey = z.enum(TERMINOLOGY_KEYS).openapi("TerminologyKey");

export const TerminologyEntry = z
  .object({
    term_key: TerminologyKey,
    label: z.string(),
    source: TerminologySource,
  })
  .openapi("TerminologyEntry");

export const TerminologyListResponse = z
  .object({ items: z.array(TerminologyEntry) })
  .openapi("TerminologyListResponse");

export const TerminologyUpsertRequest = z
  .object({
    locale: TerminologyLocale,
    label: z.string().min(1).max(80),
  })
  .openapi("TerminologyUpsertRequest");

// --- imports (spec 02 §2.6, §2.7, §5) ---

export const ImportKind = z.enum([
  "products",
  "materials",
  "boms",
  "routings",
  "customers",
  "suppliers",
  "opening_stock",
  "open_sales_orders",
  "open_purchase_orders",
  "machines",
  "people",
]);
export const ImportStatus = z.enum([
  "uploaded",
  "parsing",
  "mapping",
  "validated",
  "committing",
  "committed",
  "failed",
  "discarded",
]);
export const ImportDuplicatePolicy = z.enum(["reject", "update", "skip"]);
export const ImportRowStatus = z.enum([
  "pending",
  "valid",
  "warning",
  "error",
  "committed",
  "skipped",
]);

export const ImportJob = z
  .object({
    id: Uuid,
    facility_id: Uuid.nullable(),
    kind: ImportKind,
    status: ImportStatus,
    file_name: z.string(),
    file_size_bytes: z.number().int().min(0),
    column_mapping: z.record(z.string(), z.string()),
    total_rows: z.number().int().min(0),
    valid_rows: z.number().int().min(0),
    warning_rows: z.number().int().min(0),
    error_rows: z.number().int().min(0),
    committed_rows: z.number().int().min(0),
    duplicate_policy: ImportDuplicatePolicy,
    error_report_url: z.string().nullable().openapi({
      description:
        "Where the job's error report is read. For every kind but `customers`, a presigned GET of the report stored at validate (and regenerated by commit-stage errors), null while there is none, and null for a reader the kind's handler withholds a column from (spec 02 §7). For a `customers` job, since P3-E2 PR E2-2c (D-332 ruling 2, which answered spec 09 §12 question 16), the report is never stored: this is the API path `/v1/imports/{id}/error-report` whenever the job has error rows and null otherwise, and that route renders the workbook for its reader from the job's current rows, leaving out every column withheld from that reader.",
    }),
    created_by_user_id: Uuid,
    created_at: isoDateTime(),
    validated_at: isoDateTime().nullable(),
    committed_at: isoDateTime().nullable(),
    committed_by_user_id: Uuid.nullable(),
  })
  .openapi("ImportJob");

export const ImportJobListResponse = z
  // next_cursor is an OPAQUE pagination token (base64url of `createdAt|id`), not
  // a UUID — typing it as a plain string keeps the generated api-client honest.
  .object({ items: z.array(ImportJob), next_cursor: z.string().nullable() })
  .openapi("ImportJobListResponse");

export const ImportCreateRequest = z
  .object({
    kind: ImportKind,
    facility_id: Uuid.nullable().optional(),
    file_name: z.string().min(1).max(255),
    // 200 MB cap (D-025) mirrors the import_jobs.file_size_bytes CHECK.
    file_size_bytes: z.number().int().min(1).max(209715200),
    duplicate_policy: ImportDuplicatePolicy.optional(),
  })
  .openapi("ImportCreateRequest");

export const ImportCreateResponse = z
  .object({
    job: ImportJob,
    /** Presigned S3 PUT (15-min expiry, spec 02 §9.10). */
    upload_url: z.string(),
  })
  .openapi("ImportCreateResponse");

export const ImportMappingPatchRequest = z
  .object({ column_mapping: z.record(z.string(), z.string()) })
  .openapi("ImportMappingPatchRequest");

/**
 * What a validated row will do at commit (spec 02 §3.3's warnings-and-action
 * paragraph, P3-E2 PR E2-2c, D-325 ruling 4): `create` a new record, `update`
 * the one record its key matched under the `update` policy, or `skip` it under
 * the `skip` policy. A handler that reports no action, as the catalogue and
 * inventory handlers do, leaves the row's `action` absent.
 */
export const ImportRowAction = z.enum(["create", "update", "skip"]).openapi("ImportRowAction");

/**
 * The row message codes the `customers` lane writes into `messages[].code`
 * (spec 09 §3.15, spec 02 §3.3's customers row; P3-E2 PR E2-2c, D-325 rulings
 * 3 to 9, D-332 rulings 1 and 3). Row messages are per-row entries of a 200
 * read, NOT RFC 9457 problem `code`s, which is why they live here beside
 * `ImportRow` rather than in `onboardingProblemCodes`, and why `code` stays a
 * free string on the wire: the other lanes keep their own catalogs
 * (`apps/api/src/modules/catalogue/import/import.validators.ts`, the opening
 * stock lane's lower-case codes). Exported so the module-catalog parity gate
 * can hold the lane's raise sites against the contract's own list, the
 * `priceListRowRejectionCodes` shape. Besides these, a row body that fails the
 * route's own request schema (`CustomerCreateRequest`, or
 * `CustomerUpdateRequest` over the changed fields) is an error carrying the
 * zod issue's own code in the wire vocabulary (`too_big`, `too_small`,
 * `invalid_enum_value`, `invalid_string`, `invalid_type`, `custom` and the
 * rest of `WIRE_ISSUE_CODES`, read through `wireIssueCode`) on the column the
 * issue came from, with the `message_key` `validation.<code>` that a form's
 * 400 carries.
 *
 * ERRORS: `CUSTOMER_NOT_FOUND` (a `customer_no` the importer's customer door
 * does not answer), `CUSTOMER_ARCHIVED` (a `customer_no` naming an archived
 * customer, with `params.merged_into_customer_id` when it was merged),
 * `AMBIGUOUS_NATURAL_KEY` (several active customers the importer can open hold
 * the row's `tax_id`), `duplicate_in_db` (one match under the `reject`
 * policy), `OWNER_NOT_MEMBER`, `OWNER_NOT_ALLOWED`, `PERMISSION_DENIED` (a
 * credit cell on a create row from an importer with no credit lineage, D-332
 * ruling 1), `IDENTITY_TAKEN`, `TAG_ARCHIVED` (a tag archived between the
 * commit's read and its attach), `money_not_integer` (a `credit_limit` with
 * more decimals than its currency holds), `REQUIRED` (an empty required cell,
 * or a derived display name longer than 200 characters) and
 * `IMPORT_IMPORTER_NOT_PERMITTED` (the commit's own check of the job's creator,
 * written on every committable row).
 *
 * WARNINGS (`customersImportRowWarningCodes`): `POSSIBLE_DUPLICATE` (a create
 * row's names match a customer the importer can open), `TAG_UNKNOWN` (a name
 * in `tags` matches no active tag and is skipped), `IGNORED_ON_UPDATE` (a
 * contact or owner cell on an update row, which an update does not write) and
 * `NOTE_REDACTED` (the row's `notes` cell was emptied because a note holding
 * the same words was redacted, D-332 ruling 3). A warning row commits.
 */
export const customersImportRowCodes = [
  "CUSTOMER_NOT_FOUND",
  "CUSTOMER_ARCHIVED",
  "AMBIGUOUS_NATURAL_KEY",
  "duplicate_in_db",
  "OWNER_NOT_MEMBER",
  "OWNER_NOT_ALLOWED",
  "PERMISSION_DENIED",
  "IDENTITY_TAKEN",
  "TAG_ARCHIVED",
  "money_not_integer",
  "REQUIRED",
  "IMPORT_IMPORTER_NOT_PERMITTED",
  "POSSIBLE_DUPLICATE",
  "TAG_UNKNOWN",
  "IGNORED_ON_UPDATE",
  "NOTE_REDACTED",
] as const;
export type CustomersImportRowCode = (typeof customersImportRowCodes)[number];

/** The `customers` lane's codes that are row WARNINGS (severity `warning`);
 * every other code in `customersImportRowCodes` is an error. */
export const customersImportRowWarningCodes = [
  "POSSIBLE_DUPLICATE",
  "TAG_UNKNOWN",
  "IGNORED_ON_UPDATE",
  "NOTE_REDACTED",
] as const satisfies readonly CustomersImportRowCode[];

/**
 * The `open_sales_orders` lane's row codes (spec 09 §3.15b, D-353 ruling 2,
 * D-358). Every one is an error; the lane writes no warning. `VAT_MODE_MIXED`,
 * `PERMISSION_DENIED`, `RESOURCE_BUSY` and `ORDER_NOT_WRITTEN` arise at the
 * commit alone (D-358 rulings 2, 10 and 13). A malformed cell also carries
 * the zod issue's own code (`too_small`, `too_big`, `invalid_string`), which
 * is a validation code and not listed here.
 */
export const openSalesOrdersImportRowCodes = [
  "REQUIRED",
  "CUSTOMER_NOT_FOUND",
  "CUSTOMER_ARCHIVED",
  "AMBIGUOUS_NATURAL_KEY",
  "PRODUCT_NOT_FOUND",
  "PRODUCT_ARCHIVED",
  "UOM_NOT_FOUND",
  "UOM_NOT_CONVERTIBLE",
  "FACILITY_NOT_FOUND",
  "FACILITY_NOT_ELIGIBLE",
  "FACILITY_UNRESOLVED",
  "ORDERED_ON_IN_FUTURE",
  "PROMISE_DATE_BEFORE_ORDERED_ON",
  "money_not_integer",
  "AMBIGUOUS_DECIMAL",
  "CURRENCY_MISMATCH",
  "GROUP_HEADER_CONFLICT",
  "ORDER_REF_ALREADY_IMPORTED",
  "VAT_MODE_MIXED",
  "PERMISSION_DENIED",
  "IMPORT_IMPORTER_NOT_PERMITTED",
  "RESOURCE_BUSY",
  "ORDER_NOT_WRITTEN",
] as const;
export type OpenSalesOrdersImportRowCode = (typeof openSalesOrdersImportRowCodes)[number];

/**
 * A staged import row (spec 02 §2.7). `raw`/`normalized` are the source +
 * contract-shaped payloads; money fields inside them (`value_minor`,
 * `unit_cost_minor`, `price_minor`) are redacted server-side for roles without
 * cost visibility (§7) — the row list envelope reports `redacted_fields`. A
 * `customers` row also has its `credit_limit` cell, and every source header
 * mapped onto it, nulled for a reader spec 09 §7 withholds the limit from
 * (spec 02 §7, D-325 ruling 6). Since P3-E2 PR E2-2c a row may carry
 * `action` (`ImportRowAction`) and warning messages.
 */
export const ImportRow = z
  .object({
    id: Uuid,
    row_no: z.number().int().min(1),
    raw: z.record(z.string(), z.unknown()),
    normalized: z.record(z.string(), z.unknown()).nullable(),
    status: ImportRowStatus,
    messages: z.array(
      z.object({
        severity: z.enum(["error", "warning"]),
        field: z.string().nullable(),
        code: z.string().openapi({
          description:
            "The row's machine reason. A free string on the wire, because each lane owns its catalog: the catalogue lanes' UPPER_SNAKE codes (spec 02 §2.7), the opening-stock lane's lower-case codes, and the `customers` lane's `customersImportRowCodes` (spec 09 §3.15) together with the zod issue codes a failed request-schema parse carries.",
        }),
        message_key: z.string(),
        params: z.record(z.string(), z.unknown()).optional(),
      }),
    ),
    action: ImportRowAction.optional().openapi({
      description:
        "What the row will do at commit, as validate judged it (spec 02 §3.3, D-325 ruling 4). Present only on a lane that reports it, the `customers` lane since P3-E2 PR E2-2c, and absent on every other lane.",
    }),
    created_entity_type: z.string().nullable(),
    created_entity_id: Uuid.nullable(),
  })
  .openapi("ImportRow");

export const ImportRowListResponse = z
  .object({
    items: z.array(ImportRow),
    // Opaque row cursor (the stringified row_no), not a UUID.
    next_cursor: z.string().nullable(),
    /** Field paths the server redacted on this response (spec 02 §7). */
    redacted_fields: z.array(z.string()),
  })
  .openapi("ImportRowListResponse");

export const ImportCommitRequest = z
  .object({ allow_partial: z.boolean().optional() })
  .openapi("ImportCommitRequest");

// --- plant map (spec 02 §3.4, §5) ---

/**
 * A work center's `name_i18n` bundle, carried on the draft so the commit can
 * round-trip it VERBATIM (spec 02 §3.4 "the draft carries the bundle").
 *
 * EVERY locale is optional here — unlike module 04/05's `en`-required bundles.
 * A work center is not authored through a per-locale form: it is created by a
 * plant-map commit in whatever locale its author was working in, so a Russian
 * tenant's tree legitimately carries `{ru}` and nothing else. Demanding `en`
 * would force the client to invent an English name for every Russian line.
 */
const PlantWorkCenterNameI18n = z.object(localeKeys(z.string().min(1).optional())).openapi({
  description:
    "The work center's name per locale, echoed back from the committed tree; every locale optional",
});

const PlantWorkCenterDraft = z.object({
  name: z.string(),
  code: z.string(),
  /**
   * The bundle this draft node was LOADED from, echoed back unchanged when the
   * name was not edited (and with only the authoring locale's slot replaced
   * when it was). Optional: an agent-written or hand-written draft carries only
   * `name`, and the server then falls back to writing the tenant's default
   * locale slot, exactly as it did before this field existed.
   *
   * It exists because the flattened `name` alone cannot round-trip. The editor
   * resolves a bundle to ONE string in the BROWSER's locale; the server used to
   * write that string back into the TENANT's default locale slot. Loading
   * translated ru → en and committing pasted the English back over the Russian:
   * a commit that edited nothing rewrote every work center's Russian name
   * (13 rows on the design partner's seed). With the bundle on the wire the
   * round trip is an identity by construction — no shared resolution rule has
   * to be kept in step across the boundary.
   */
  name_i18n: PlantWorkCenterNameI18n.optional(),
  template_ref: z.string().nullable().optional(),
  machine_count: z.number().int().min(0).optional(),
});
const PlantStorageLocationDraft = z.object({
  name: z.string(),
  code: z.string(),
  parent_code: z.string().nullable().optional(),
});
const PlantDepartmentDraft = z.object({
  name: z.string(),
  code: z.string(),
  work_centers: z.array(PlantWorkCenterDraft),
});
const PlantFacilityDraft = z.object({
  name: z.string(),
  code: z.string(),
  timezone: z.string(),
  address: z.string().nullable().optional(),
  storage_locations: z.array(PlantStorageLocationDraft),
  departments: z.array(PlantDepartmentDraft),
});

export const PlantMapDraft = z
  .object({ facilities: z.array(PlantFacilityDraft) })
  .openapi("PlantMapDraft");

export const PlantMapCommitRequest = z
  .object({
    draft: PlantMapDraft,
    // Explicit-delete confirm (spec 02 §3.4: "deletions are never implicit").
    // Default false ⇒ a node present in DB but absent from the draft is left
    // untouched (a half-finished draft never deletes real entities). Set true ⇒
    // an absent node is a confirmed deletion: an UNREFERENCED (empty/leaf) node
    // is archived through Module 16 (counted in `deleted`); a still-referenced
    // node (a facility/department with active children or members) is refused
    // with 409 ENTITY_IN_USE (no writes).
    confirm_deletions: z.boolean().optional(),
  })
  .openapi("PlantMapCommitRequest");

// An owning-module entity type that the commit could not materialise because its
// module is not wired into this deployment yet (spec 02 §3.4). storage_locations
// (Module 06) and machines (Module 08) are UNBOUND ports here — recorded pending,
// never faked. Flips to a real created count with zero rework once the module binds.
const PlantMapPendingEntity = z.object({
  entity_type: z.string(),
  owner_module: z.string(),
  count: z.number().int().min(0),
  reason: z.string(),
});

export const PlantMapCommitResponse = z
  .object({
    created: z.object({
      facilities: z.number().int().min(0),
      departments: z.number().int().min(0),
      work_centers: z.number().int().min(0),
      machines: z.number().int().min(0),
      storage_locations: z.number().int().min(0),
    }),
    updated: z.number().int().min(0),
    // Confirmed deletions archived through Module 16 this commit (facilities +
    // departments + work centers; §3.4). storage_locations/machines are never
    // deleted here (unbound ports).
    deleted: z.number().int().min(0),
    // ALWAYS EMPTY on the 200 success body: a still-referenced confirmed
    // deletion surfaces as a 409 ENTITY_IN_USE with the refusals under
    // `extensions.refused[]` (§3.4), so the commit never partially applies.
    // Retained in the success schema for §5 shape parity; the two-layer scheme
    // is an UPPER_SNAKE HTTP problem code (ENTITY_IN_USE) carrying lowercase
    // domain refusal codes (`entity_in_use`) in each refused[] entry.
    refused: z
      .array(z.object({ code: z.string(), path: z.string(), reason: z.string() }))
      .openapi({ description: "Always empty on 200; refusals surface as 409 ENTITY_IN_USE." }),
    // Honest-pending owning-module entities (unbound ports, §3.4). Empty once
    // Modules 06/08 wire in.
    pending: z.array(PlantMapPendingEntity),
  })
  .openapi("PlantMapCommitResponse");

// --- QR print kits (spec 02 §2.8, §5) ---

export const QrKitKind = z.enum(["work_center_stations", "stock_unit_labels", "location_labels"]);
export const QrKitStatus = z.enum(["queued", "rendering", "ready", "failed"]);
export const QrKitPageSize = z.enum(["a4", "a5", "letter"]);

export const QrPrintBatch = z
  .object({
    id: Uuid,
    facility_id: Uuid.nullable(),
    kind: QrKitKind,
    status: QrKitStatus,
    label_template_ref: z.string(),
    page_size: QrKitPageSize,
    labels_per_page: z.number().int().min(1).max(24),
    item_count: z.number().int().min(0),
    params: z.record(z.string(), z.unknown()),
    pdf_url: z.string().nullable(),
    error: z.record(z.string(), z.unknown()).nullable(),
    requested_by_user_id: Uuid,
    created_at: isoDateTime(),
    ready_at: isoDateTime().nullable(),
  })
  .openapi("QrPrintBatch");

export const QrPrintBatchListResponse = z
  .object({ items: z.array(QrPrintBatch), next_cursor: Uuid.nullable() })
  .openapi("QrPrintBatchListResponse");

export const QrPrintItem = z
  .object({
    id: Uuid,
    entity_type: z.enum(["work_center", "stock_unit", "location"]),
    entity_id: Uuid,
    /** The canonical scan path, MINTED BY THE OWNING MODULE (spec 02 §2.9) and
     * frozen here at render time — onboarding renders, it never mints. */
    qr_payload: z.string(),
    /** The display lines exactly as printed (frozen snapshot, §3.4). */
    label_lines: z.array(z.string()),
    page_no: z.number().int().min(1),
  })
  .openapi("QrPrintItem");

export const QrPrintItemListResponse = z
  .object({ items: z.array(QrPrintItem), truncated: z.boolean() })
  .openapi("QrPrintItemListResponse");

export const QrKitCreateRequest = z
  .object({
    kind: QrKitKind,
    facility_id: Uuid.nullable().optional(),
    label_template_ref: z.string().min(1),
    page_size: QrKitPageSize.optional(),
    params: z.record(z.string(), z.unknown()),
  })
  .openapi("QrKitCreateRequest");

// --- go-live checklist (spec 02 §2.10, §3.6, §5) ---

export const ChecklistCategory = z.enum([
  "vertical",
  "quality", // D-214
  "catalogue",
  "plant",
  "people",
  "opening",
  "channels",
]);
export const ChecklistItemKind = z.enum(["auto", "manual"]);
export const ChecklistItemStatus = z.enum(["pending", "passed", "failed", "waived"]);

export const OnboardingChecklistItem = z
  .object({
    item_key: z.string(),
    category: ChecklistCategory,
    kind: ChecklistItemKind,
    blocking: z.boolean(),
    weight: z.number().int().min(1).max(10),
    status: ChecklistItemStatus,
    detail: z.record(z.string(), z.unknown()),
    waived_by_user_id: Uuid.nullable(),
    waived_reason: z.string().nullable(),
    evaluated_at: isoDateTime().nullable(),
  })
  .openapi("OnboardingChecklistItem");

export const OnboardingChecklistResponse = z
  .object({
    score: z.number().int().min(0).max(100),
    /** Blocking items not passed/waived — the §3.6 go-live gate's first half. */
    blocking_unmet: z.array(z.string()),
    /** True when every blocking item is met AND score >= 85 (§3.6). */
    go_live_eligible: z.boolean(),
    items: z.array(OnboardingChecklistItem),
  })
  .openapi("OnboardingChecklistResponse");

export const ChecklistWaiveRequest = z
  .object({ reason: z.string().min(10) })
  .openapi("ChecklistWaiveRequest");

export const ChecklistManualSetRequest = z
  .object({ status: z.enum(["passed", "failed"]) })
  .openapi("ChecklistManualSetRequest");

export const GoLiveResponse = z
  .object({ session: OnboardingSession, score: z.number().int().min(0).max(100) })
  .openapi("GoLiveResponse");

/** An opening posting value (spec 02 §3.5) — integer minor units + currency.
 * Exported so the opening-stock import committer + Module 11 JE share the type;
 * money is redacted from import previews for non-finance roles (§7). */
export const OpeningValue = z
  .object({ value_minor: MinorMoney.min(0), currency: Currency })
  .openapi("OpeningValue");

/**
 * Problem codes the module-02 onboarding surface raises (UPPER_SNAKE, RFC 9457;
 * D-022). Mirrors the spec §5/§9 canonical set: pack install (§3.2), import
 * pipeline (§3.3, §9), plant map (§3.4), opening state (§3.5), and go-live
 * (§3.6) failure modes. Field-error cell codes inside import rows (§2.7
 * `messages[]`: each lane's own catalog, the catalogue lanes' in
 * `apps/api/src/modules/catalogue/import/import.validators.ts` and the
 * `customers` lane's `customersImportRowCodes` above) are NOT top-level
 * problem codes and are intentionally excluded. (This comment named a file
 * `import_error_codes.ts` that has never existed until E2-2c corrected it.)
 */
export const onboardingProblemCodes = [
  // session / steps
  "SESSION_NOT_FOUND",
  "PRECONDITIONS_UNMET",
  "STALE_PAYLOAD",
  "INVALID_STEP",
  // pack install
  "PACK_UNKNOWN",
  "PACK_ALREADY_INSTALLED",
  "PACK_DISABLED",
  "PACK_INSTALL_IN_PROGRESS",
  "PACK_INSTALL_NOT_FOUND",
  "PACK_SNAPSHOT_TOO_LARGE",
  // previewed pack update (D-343): the preview was read against a pack that is
  // no longer the deployed one; an accepted change the preview does not list
  // as acceptable; nothing to apply
  "PACK_UPDATE_STALE",
  "PACK_UPDATE_NOT_ACCEPTABLE",
  "PACK_UP_TO_DATE",
  // imports
  "IMPORT_JOB_NOT_FOUND",
  "IMPORT_STATE_INVALID",
  "IMPORT_KIND_NOT_AVAILABLE",
  // P3-E2 PR E2-2c (spec 02 §3.3's staged-slot paragraph, spec 09 §3.15,
  // D-325 ruling 2): 422 on `POST /v1/imports/{id}/validate` and `/commit` of
  // a `customers` job whose creator no longer holds
  // `onboarding.import.customers`, `sales.manage_customers` or a customer
  // write lineage, whoever pressed the door; the job does not change. The
  // worker's own check at commit start writes the same code on every
  // committable row (`customersImportRowCodes`).
  "IMPORT_IMPORTER_NOT_PERMITTED",
  "FACILITY_REQUIRED",
  "HAS_ERRORS",
  "MAPPING_INCOMPLETE",
  "BAD_ENCODING",
  "FILE_TOO_LARGE",
  "FINANCE_REQUIRED_FOR_VALUED_STOCK",
  "CURRENCY_MISMATCH",
  "AS_OF_TOO_OLD",
  "FOREIGN_CURRENCY_UNSUPPORTED",
  // plant map
  "STALE_DRAFT",
  "ENTITY_IN_USE",
  "UNKNOWN_REFERENCE",
  "DUPLICATE_CODE",
  // qr kits (D-055). WIDENED 2026-08-02 (four-slice audit): the catalog carried
  // two entries while the built surface raises thirteen, and one of the two —
  // `QR_BATCH_NOT_FOUND` — was raised by nothing at all: the D-055 slice named
  // the resource "kit" on the wire and the catalog was never brought along, so
  // the one artefact §5 tells a client to look codes up in listed a 404 code
  // that does not exist and omitted the one that does. Every code below is
  // raised by apps/api/src/modules/onboarding/qr/ and named in §3.4's
  // "Refusals (all before any PDF exists)" paragraph.
  "QR_KIT_NOT_FOUND",
  "QR_KIT_PRECONDITION_UNMET",
  "QR_KIT_KIND_NOT_AVAILABLE",
  "QR_KIT_PARAMS_INVALID",
  "QR_KIT_SELECTION_TOO_LARGE",
  "QR_KIT_SELECTION_EMPTY",
  "QR_KIT_FACILITY_REQUIRED",
  "QR_PAYLOAD_MISSING",
  // Label templates are PACK DATA (§2.8/§3.4): no install, no template, or a
  // template whose kind does not match the requested kit, or one whose text
  // cannot be rendered at the declared size — a label is never scaled to fit.
  "PACK_NOT_INSTALLED",
  "QR_LABEL_TEMPLATE_NOT_FOUND",
  "QR_LABEL_TEMPLATE_KIND_MISMATCH",
  "QR_LABEL_TEMPLATE_TOO_LARGE",
  "QR_LABEL_GLYPH_COVERAGE",
  "QR_RENDER_FAILED",
  // checklist / go-live
  "CHECKLIST_ITEM_NOT_FOUND",
  // The reason is shorter than the 10-character floor (contract, service and a
  // column CHECK all enforce it). Named for the condition, not for a property
  // of the item — every catalog item is waivable by someone (spec 02 §5).
  "WAIVE_REASON_TOO_SHORT",
  "ITEM_NOT_MANUAL",
  // A manual attestation whose VERIFIER module is not wired yet (spec 02 §3.6
  // pre-live test-step contract): passing it would be a fake check, so it is
  // refused — waiving it with a reason stays available.
  "CHECK_NOT_AVAILABLE",
  "BLOCKING_WAIVE_FORBIDDEN",
  "BLOCKING_ITEMS_UNMET",
  "SCORE_BELOW_THRESHOLD",
  "ALREADY_LIVE",
  "INVALID_CURSOR",
  // agent tools (§8) — module-02-specific refusals on the tool door. (The
  // platform-level AGENT_TOOL_NOT_AVAILABLE / AGENT_TOOL_PARAMS_INVALID /
  // AGENT_TOOL_TENANT_PARAM_FORBIDDEN belong to the agent-tool runtime, not to
  // this module's surface, and are deliberately not listed here.)
  //
  // An agent commit of `opening_stock`: that commit posts value and §7
  // co-requires the finance role, which agents cannot hold in v1 (§8).
  "AGENT_CO_PERMISSION_FORBIDDEN",
  // propose_plant_map called with only a free-text description / photo: turning
  // prose or an org-chart photo into a plant tree is Module 14's runtime, and
  // the tool never invents a factory from prose (§8).
  "AGENT_TOOL_INPUT_NOT_INTERPRETABLE",
] as const;

export type OnboardingProblemCode = (typeof onboardingProblemCodes)[number];
